import rentalAgreement from 'assets/rental_agreement.svg';
import vehicleDetails from 'assets/vehicle_details.svg';
import alerts from 'assets/alerts.svg';
import inventory from 'assets/inventory.svg';
import status from 'assets/status.svg';
import department from 'assets/department.svg';
import inspection from 'assets/inspection.svg';
import note from 'assets/note.svg';
import connect from 'assets/connect.svg';
import { fromJS } from 'immutable';

export const VEHICLE_OWNERS = {
  fleet: 2,
  inventory: 4,
};

export const VEHICLE_PAGE_CONTENT_TYPE = {
  VEHICLE_INFORMATION: 'vehicleInformation',
  FLEET_DETAILS: 'fleet',
  VEHICLE_LOCATION: 'vehicleLocation',
  CONNECT: 'connect',
  EDIT_DAMAGES: 'edit_damages',
  SET_ODOMETER: 'set_odometer',
  ADD_NOTE: 'add_note',
  CHANGE_DEPARTMENT: 'change_department',
  TELEMATICS_ENROLMENT: 'telematics_enrolment',
};

export const VEHICLE_TABS = {
  table: 'table',
  map: 'map',
  calendar: 'calendar',
};

export const VEHICLE_TABLE_NAME = {
  vehicle: 'vehicle',
  model: 'model',
  owner: 'owner',
  dealership: 'dealership',
  department: 'department',
  agreement: 'agreement',
};

export const VEHICLE_PAGE_TABS = {
  editDamages: 'edit_damages',
  addNote: 'add_note',
  setOdometer: 'set_odometer',
  telematicsEnrolment: 'telematics_enrolment',
};

export const TAB_COMPONENT_ITEMS = [
  {
    name: VEHICLE_PAGE_TABS.editDamages,
    id: 'components.TabContainter.editDamages',
  },
  {
    name: VEHICLE_PAGE_TABS.addNote,
    id: 'components.TabContainter.notes',
  },
  {
    name: VEHICLE_PAGE_TABS.setOdometer,
    id: 'components.TabContainter.initialOdometer',
  },
  {
    name: VEHICLE_PAGE_TABS.telematicsEnrolment,
    id: 'components.TabContainter.telematicsEnrolment',
  },
];

export const DAMAGES_NO_POSITION_LABEL = 'na';

const DAMAGE_POSITIONS = [
  { value: 'front_right' },
  { value: 'front_left' },
  { value: 'back_right' },
  { value: 'back_left' },
  { value: 'front' },
  { value: 'back' },
  { value: 'right' },
  { value: 'left' },
  { value: 'na' },
];

export const DAMAGE_POSITIONS_WITH_TRANSLATION = DAMAGE_POSITIONS.map((position) => ({
  value: position.value,
  label: { translationId: `components.Damages.${position.value}` },
}));

export const POSITIONS = Object.fromEntries(
  DAMAGE_POSITIONS_WITH_TRANSLATION.map((position) => [position.value, position])
);

const DAMAGES_ITEM = [
  { value: 'bumper', positions: [POSITIONS.front, POSITIONS.back] },
  {
    value: 'door',
    positions: [POSITIONS.front_right, POSITIONS.front_left, POSITIONS.back_right, POSITIONS.back_left],
  },
  {
    value: 'fender',
    positions: [POSITIONS.front_right, POSITIONS.front_left, POSITIONS.back_right, POSITIONS.back_left],
  },
  { value: 'hood', positions: [POSITIONS.na] },
  {
    value: 'light',
    positions: [POSITIONS.front_right, POSITIONS.front_left, POSITIONS.back_right, POSITIONS.back_left],
  },
  {
    value: 'other',
    positions: [
      POSITIONS.front_right,
      POSITIONS.front_left,
      POSITIONS.back_right,
      POSITIONS.back_left,
      POSITIONS.front,
      POSITIONS.back,
      POSITIONS.na,
    ],
  },
  { value: 'roof', positions: [POSITIONS.na] },
  {
    value: 'tire',
    positions: [POSITIONS.front_right, POSITIONS.front_left, POSITIONS.back_right, POSITIONS.back_left],
  },
  { value: 'trunk_lid', positions: [POSITIONS.na] },
  {
    value: 'wheel',
    positions: [POSITIONS.front_right, POSITIONS.front_left, POSITIONS.back_right, POSITIONS.back_left],
  },
  { value: 'windshield', positions: [POSITIONS.na] },
];

export const DAMAGES_ITEM_WITH_TRANSLATION = DAMAGES_ITEM.map((damage) => ({
  ...damage,
  label: { translationId: `components.Damages.${damage.value}` },
}));

export enum HistoryAction {
  UPDATED = 'updated',
  NOTE_ADDED = 'new_note',
  REMOVED = 'removed',
  ADDED = 'added',
  ENTERED = 'entered',
  EXITED = 'exited',
  SET_LOANER = 'set_loaner',
  SET_SHUTTLE = 'set_shuttle',
}

export enum HistoryCategory {
  DETAILS = 'details',
  GEOFENCE = 'geofence',
  NOTE = 'note',
  AGREEMENT = 'agreement',
  UNIT_TYPE = 'unit_type',
}

export const TIMELINE_CATEGORY = {
  details: {
    name: 'vehicle_details',
    icon: vehicleDetails,
    filterTranslation: 'components.TimelineCard.vehicle.detailsFilter',
    translation: 'components.TimelineCard.vehicle.details' as string,
    editable: false,
  },
  agreement: {
    name: 'rental_agreement',
    icon: rentalAgreement,
    filterTranslation: 'components.TimelineCard.vehicle.agreementFilter',
    translation: 'components.TimelineCard.vehicle.agreement' as string,
    editable: false,
  },
  alerts: {
    name: 'alerts',
    icon: alerts,
    filterTranslation: 'components.TimelineCard.vehicle.alertsFilter',
    translation: 'components.TimelineCard.vehicle.alerts' as string,
    editable: false,
  },
  inventory: {
    name: 'inventory',
    icon: inventory,
    filterTranslation: 'components.TimelineCard.vehicle.inventoryFilter',
    translation: 'components.TimelineCard.vehicle.inventory' as string,
    editable: false,
  },
  fleet: {
    name: 'fleet',
    filterTranslation: 'components.TimelineCard.vehicle.inventoryFilter',
    translation: 'components.TimelineCard.vehicle.inventory' as string,
    editable: false,
  },
  status: {
    name: 'status',
    icon: status,
    filterTranslation: 'components.TimelineCard.vehicle.statusFilter',
    translation: 'components.TimelineCard.vehicle.status' as string,
    editable: false,
  },
  department: {
    name: 'department',
    icon: department,
    filterTranslation: 'components.TimelineCard.vehicle.departmentFilter',
    translation: 'components.TimelineCard.vehicle.department' as string,
    editable: false,
  },
  inspection: {
    name: 'inspection',
    icon: inspection,
    filterTranslation: 'components.TimelineCard.vehicle.inspectionFilter',
    translation: 'components.TimelineCard.vehicle.inspection' as string,
    editable: false,
  },
  note: {
    name: 'note',
    icon: note,
    filterTranslation: 'components.TimelineCard.vehicle.noteFilter',
    translation: 'components.TimelineCard.vehicle.note' as string,
    editable: true,
  },
  connect: {
    name: 'connect',
    incon: connect,
    filterTranslation: 'components.TimelineCard.vehicle.connectFilter',
    translation: 'components.TimelineCard.vehicle.connect' as string,
    editable: false,
  },
  geofence: {
    name: 'geofence',
    icon: alerts,
    filterTranslation: 'components.TimelineCard.vehicle.geofenceFilter',
    translation: 'components.TimelineCard.vehicle.geofence' as string,
    editable: false,
  },
  unit_type: {
    name: 'unit_type',
    icon: vehicleDetails,
    filterTranslation: 'components.TimelineCard.vehicle.unitTypeFilter',
    translation: 'components.TimelineCard.vehicle.unitType' as string,
    editable: false,
  },
} as const;

export const VEHICLE_TIMELINE_FILTER = [
  {
    name: '',
    translation: 'components.TimelineCard.vehicle.noFilter',
  },
  {
    name: 'vehicle',
    translation: 'components.TimelineCard.vehicle.detailsFilter',
  },
  {
    name: 'rental_agreement',
    translation: 'components.TimelineCard.vehicle.agreementFilter',
  },
  {
    name: 'alerts',
    translation: 'components.TimelineCard.vehicle.alertsFilter',
  },
  {
    name: 'inventory',
    translation: 'components.TimelineCard.vehicle.inventoryFilter',
  },
  {
    name: 'status',
    translation: 'components.TimelineCard.vehicle.statusFilter',
  },
  {
    name: 'department',
    translation: 'components.TimelineCard.vehicle.departmentFilter',
  },
  {
    name: 'inspection',
    translation: 'components.TimelineCard.vehicle.inspectionFilter',
  },
  {
    name: 'note',
    translation: 'components.TimelineCard.vehicle.noteFilter',
  },
  {
    name: 'connect',
    translation: 'components.TimelineCard.vehicle.connectFilter',
  },
];

export enum VehicleUnitType {
  LOANER = 0,
  SHUTTLE = 1,
}

export const VEHICLE_TYPE_LIST_MAP = fromJS({
  [VehicleUnitType.LOANER]: { translationId: 'services.Vehicles.loaner' },
  [VehicleUnitType.SHUTTLE]: { translationId: 'services.Vehicles.shuttle' },
});
